import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {addToCart,fetchCartByUserId,updateCartItemById,deleteCartItemById, resetCartByUserId} from './CartApi'

const GUEST_CART_STORAGE_KEY='guest_cart'

const getCartItemProductId=(cartItem)=>cartItem?.product?._id || cartItem?.product?.id || cartItem?.product

const getGuestCartItemProduct=(item)=>item?.productData || (typeof item?.product==='object' ? item.product : {_id:item?.product})

const isGuestCartItemId=(cartItemId)=>typeof cartItemId==='string' && cartItemId.startsWith('guest-')

const getGuestCartItems=()=>JSON.parse(localStorage.getItem(GUEST_CART_STORAGE_KEY) || '[]')

const saveGuestCartItems=(items)=>{
    localStorage.setItem(GUEST_CART_STORAGE_KEY,JSON.stringify(items))
}

const initialState={
    status:"idle",
    items:[],
    cartItemAddStatus:"idle",
    cartItemRemoveStatus:"idle",
    errors:null,
    successMessage:null
}

export const addToCartAsync=createAsyncThunk('cart/addToCartAsync',async(item,{getState})=>{
    const loggedInUser=getState()?.AuthSlice?.loggedInUser

    if(loggedInUser?._id){
        const addedItem=await addToCart(item)
        return addedItem
    }

    const guestCart=JSON.parse(localStorage.getItem(GUEST_CART_STORAGE_KEY) || '[]')
    const guestProduct=getGuestCartItemProduct(item)
    const guestProductId=getCartItemProductId({product:guestProduct})
    const guestCartItem={
        _id:item?._id || `guest-${Date.now()}`,
        product:guestProduct,
        quantity:item?.quantity ?? 1,
    }

    const existingIndex=guestCart.findIndex((cartItem)=>getCartItemProductId(cartItem)===guestProductId)

    if(existingIndex>-1){
        guestCart[existingIndex]={
            ...guestCart[existingIndex],
            ...guestCartItem,
            quantity:(guestCart[existingIndex].quantity || 0) + guestCartItem.quantity,
        }
        localStorage.setItem(GUEST_CART_STORAGE_KEY,JSON.stringify(guestCart))
        return guestCart[existingIndex]
    }

    guestCart.push(guestCartItem)
    localStorage.setItem(GUEST_CART_STORAGE_KEY,JSON.stringify(guestCart))
    return guestCartItem
})
export const fetchCartByUserIdAsync=createAsyncThunk('cart/fetchCartByUserIdAsync',async(id)=>{
    const items=await fetchCartByUserId(id)
    return items
})
export const updateCartItemByIdAsync=createAsyncThunk('cart/updateCartItemByIdAsync',async(update)=>{
    if(isGuestCartItemId(update?._id)){
        const guestCart=getGuestCartItems()
        const updatedGuestCart=guestCart.map((cartItem)=>{
            if(cartItem._id===update._id){
                return {...cartItem,...update}
            }
            return cartItem
        })

        saveGuestCartItems(updatedGuestCart)
        return updatedGuestCart.find((cartItem)=>cartItem._id===update._id) || update
    }

    const updatedItem=await updateCartItemById(update)
    return updatedItem
})
export const deleteCartItemByIdAsync=createAsyncThunk('cart/deleteCartItemByIdAsync',async(id)=>{
    if(isGuestCartItemId(id)){
        const guestCart=getGuestCartItems()
        const deletedGuestItem=guestCart.find((cartItem)=>cartItem._id===id)
        const updatedGuestCart=guestCart.filter((cartItem)=>cartItem._id!==id)

        saveGuestCartItems(updatedGuestCart)
        return deletedGuestItem || {_id:id}
    }

    const deletedItem=await deleteCartItemById(id)
    return deletedItem
})
export const resetCartByUserIdAsync=createAsyncThunk('cart/resetCartByUserIdAsync',async(userId)=>{
    const updatedCart=await resetCartByUserId(userId)
    return updatedCart
})

const cartSlice=createSlice({
    name:"cartSlice",
    initialState:initialState,
    reducers:{
        resetCartItemAddStatus:(state)=>{
            state.cartItemAddStatus='idle'
        },
        // add an item to cart state for unauthenticated (guest) users
        addGuestCartItem:(state, action) => {
            state.items.push(action.payload)
            state.cartItemAddStatus = 'fulfilled'
        },
        // replace cart items (useful to hydrate guest cart on app load)
        setCartItems:(state, action) => {
            state.items = action.payload
        },
        resetCartItemRemoveStatus:(state)=>{
            state.cartItemRemoveStatus='idle'
        }
    },
    extraReducers:(builder)=>{
        builder
            .addCase(addToCartAsync.pending,(state)=>{
                state.cartItemAddStatus='pending'
            })
            .addCase(addToCartAsync.fulfilled,(state,action)=>{
                state.cartItemAddStatus='fulfilled'
                const existingIndex=state.items.findIndex((item)=>item._id===action.payload._id)

                if(existingIndex>-1){
                    state.items[existingIndex]=action.payload
                }
                else{
                    state.items.push(action.payload)
                }
            })
            .addCase(addToCartAsync.rejected,(state,action)=>{
                state.cartItemAddStatus='rejected'
                state.errors=action.error
            })

            .addCase(fetchCartByUserIdAsync.pending,(state)=>{
                state.status='pending'
            })
            .addCase(fetchCartByUserIdAsync.fulfilled,(state,action)=>{
                state.status='fulfilled'
                state.items=(action.payload || []).filter((item)=>item?.product)
            })
            .addCase(fetchCartByUserIdAsync.rejected,(state,action)=>{
                state.status='rejected'
                state.errors=action.error
            })

            .addCase(updateCartItemByIdAsync.pending,(state)=>{
                state.status='pending'
            })
            .addCase(updateCartItemByIdAsync.fulfilled,(state,action)=>{
                state.status='fulfilled'
                const index=state.items.findIndex((item)=>item._id===action.payload._id)
                state.items[index]=action.payload
            })
            .addCase(updateCartItemByIdAsync.rejected,(state,action)=>{
                state.status='rejected'
                state.errors=action.error
            })

            .addCase(deleteCartItemByIdAsync.pending,(state)=>{
                state.cartItemRemoveStatus='pending'
            })
            .addCase(deleteCartItemByIdAsync.fulfilled,(state,action)=>{
                state.cartItemRemoveStatus='fulfilled'
                state.items=state.items.filter((item)=>item._id!==action.payload._id)
            })
            .addCase(deleteCartItemByIdAsync.rejected,(state,action)=>{
                state.cartItemRemoveStatus='rejected'
                state.errors=action.error
            })

            .addCase(resetCartByUserIdAsync.pending,(state)=>{
                state.status='pending'
            })
            .addCase(resetCartByUserIdAsync.fulfilled,(state)=>{
                state.status='fulfilled'
                state.items=[]
            })
            .addCase(resetCartByUserIdAsync.rejected,(state,action)=>{
                state.status='rejected'
                state.errors=action.error
            })
    }
})

// exporting selectors
export const selectCartStatus=(state)=>state.CartSlice.status
export const selectCartItems=(state)=>state.CartSlice.items
export const selectCartErrors=(state)=>state.CartSlice.errors
export const selectCartSuccessMessage=(state)=>state.CartSlice.successMessage
export const selectCartItemAddStatus=(state)=>state.CartSlice.cartItemAddStatus
export const selectCartItemRemoveStatus=(state)=>state.CartSlice.cartItemRemoveStatus

// exporting reducers
export const {resetCartItemAddStatus,resetCartItemRemoveStatus,addGuestCartItem,setCartItems}=cartSlice.actions

export default cartSlice.reducer
